/**
 * A cargo barge — the thing being delivered.
 *
 * No propulsion of its own; it moves only because something pulls it.
 *
 * Deliberately arcade-tuned: the displacement says 1,400 tonnes, the physics
 * says a third of that, and the water grips it hard. A real barge carries its
 * way for half a kilometre and swings wide on every turn; this one slows down
 * when you stop pulling and stays roughly where you left it, because the
 * player is five and "the barge drifted into the breakwater while I lined up"
 * is not a fun lesson in momentum.
 */

import { Group, Vector3 } from 'three/webgpu'
import { HULL_WATER_PROFILES, type HullWaterSource } from '../world/HullWaterMasks.ts'
import { Vessel } from '../physics/Vessel.ts'
import { buildBargeHull, type HullAttachments } from './hulls.ts'
import { disposeBargeModel, loadContainerBargeModel } from './containerBargeModel.ts'

export class Barge extends Vessel {
  readonly group: Group
  readonly attachments: HullAttachments
  readonly ready: Promise<void>
  readonly waterExclusion: HullWaterSource
  private disposed = false

  constructor(seed = 1) {
    super({
      name: 'barge',
      mass: 450_000,
      length: 220,
      beam: 28,
      draft: 3.0,
      dragForward: 9_000,
      dragLateral: 260_000,
      dragYaw: 5.5e7,
      lateralResistanceOffset: 0.5,
      buoyancySamples: [7, 3],
      pitchDamping: 1.0,
      rollDamping: 0.95,
    })

    const { group, attachments } = buildBargeHull(seed)
    this.group = new Group()
    this.group.name = 'barge'
    this.waterExclusion = { object: this.group, profile: HULL_WATER_PROFILES['barge-fallback'] }
    this.group.add(group)
    this.attachments = attachments
    this.ready = loadContainerBargeModel(seed).then(model => {
      if (this.disposed) {
        disposeBargeModel(model)
        return
      }
      this.group.remove(group)
      disposeBargeModel(group)
      this.group.add(model)
      this.waterExclusion.profile = HULL_WATER_PROFILES.barge
    }).catch(error => {
      if (!this.disposed) console.warn('Container barge GLB failed to load; keeping the procedural hull:', error)
    })
  }

  /** Contracts and mode changes can finish before the model download does. */
  dispose(): void {
    this.disposed = true
    disposeBargeModel(this.group)
  }

  syncVisual(): void {
    this.group.position.copy(this.position)
    this.group.quaternion.copy(this.quaternion)
  }

  /** World-space bow bitt — where the hawser hooks on. */
  towPointWorld(target: Vector3): Vector3 {
    return this.localToWorld(this.attachments.towPoint, target)
  }
}
